import { apiRequest } from "src/async/apiUtils";
import { apiUrl } from "src/config/host";

// Activity inside embedded iframes, shaped like a record in the admin API's `activities` collection. The important
// events are stored in the `apps_activities` collection, sent in batches. Nothing is logged to the console.

// The events worth storing: what the user opened, entered and changed. Clicks are not stored, and neither are API
// calls, pointer and focus movement, tab visibility, loads or on-screen changes.
const STORED_ACTIONS = new Set(["OPEN", "NAVIGATION_CHANGE", "FIELD_UPDATE", "FORM_SUBMIT", "DRAG_AND_DROP", "DATA_UPDATE"]);
const FLUSH_DELAY_MS = 5000;
const BATCH_SIZE = 20;
const KEEPALIVE_MAX_BYTES = 60000; // browsers refuse larger keepalive requests

let currentUser = null;
let currentPage = null;
const sessions = new WeakMap(); // iframe element -> { id, sequence, startedAt }
let queue = []; // [{ record, route }] waiting to be stored
let flushTimer = null;

export const setIframeActivityUser = (user) => {
  currentUser = user || null;
};

// { workspace_id, workspace_name, group_id, group_name, page_id, page_name, form_id, form_name, document_id,
//   document_name, page_type, route } of the portal page showing the iframes. `route` is the workspace, group and
// page (or form/document) as they appear in the portal's URL, which the API uses to allow public pages.
export const setIframeActivityPage = (page) => {
  currentPage = page || null;
};

// One session per iframe element: it starts when the portal first logs something for that iframe.
function sessionFor(iframe) {
  let session = sessions.get(iframe);
  if (!session) {
    session = { id: `ifr_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`, sequence: 0, startedAt: Date.now() };
    sessions.set(iframe, session);
  }
  return session;
}

function originOf(url) {
  try {
    return new URL(url, window.location.href).origin;
  } catch (error) {
    return null;
  }
}

function clientInfo() {
  return {
    user_agent: navigator.userAgent,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    screen: `${window.screen.width}x${window.screen.height}`,
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    device_pixel_ratio: window.devicePixelRatio,
    online: navigator.onLine,
  };
}

// socket.io's own background traffic is not something the user did.
const isNoise = (action, details) => action === "DATA_UPDATE" && /socket\.io/.test(`${details?.via || ""} ${details?.resource || ""}`);

const routeKey = (route) => JSON.stringify(route || {});

function send(records, route, keepalive) {
  const queries = Object.fromEntries(Object.entries(route || {}).filter(([, value]) => value));
  const body = { activities: records };

  if (!keepalive) {
    apiRequest("POST", "/portal/apps-activities", { body, queries }, { is_abort: false, returnErrorWithstatus: true }).catch(() => {});
    return;
  }

  // The page is going away: only a keepalive request is still sent, and only if it is small enough.
  const json = JSON.stringify(body);
  if (json.length > KEEPALIVE_MAX_BYTES && records.length > 1) {
    const half = Math.ceil(records.length / 2);
    send(records.slice(0, half), route, true);
    send(records.slice(half), route, true);
    return;
  }
  const token = localStorage.getItem("accessToken");
  const loginWorkspaceId = localStorage.getItem("loginWorkspaceId");
  const query = new URLSearchParams(queries).toString();
  fetch(`${apiUrl}/portal/apps-activities${query ? `?${query}` : ""}`, {
    method: "POST",
    keepalive: true,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(loginWorkspaceId ? { "x-workspace-id": loginWorkspaceId } : {}),
    },
    body: json,
  }).catch(() => {});
}

// Sends everything waiting to be stored, in batches of up to BATCH_SIZE records for the same portal page.
export function flushIframeActivity({ keepalive = false } = {}) {
  clearTimeout(flushTimer);
  flushTimer = null;
  while (queue.length) {
    const { route } = queue[0];
    const batch = [];
    while (queue.length && batch.length < BATCH_SIZE && routeKey(queue[0].route) === routeKey(route)) batch.push(queue.shift().record);
    send(batch, route, keepalive);
  }
}

if (typeof window !== "undefined") {
  window.addEventListener("pagehide", () => flushIframeActivity({ keepalive: true }));
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushIframeActivity({ keepalive: true });
  });
}

function store(record, action, details, route) {
  if (!STORED_ACTIONS.has(action) || isNoise(action, details)) return;
  queue.push({ record, route });
  if (queue.length >= BATCH_SIZE) flushIframeActivity();
  else if (!flushTimer) flushTimer = setTimeout(flushIframeActivity, FLUSH_DELAY_MS);
}

// A frame removed before its first load was a portal re-render, not an app visit.
export function discardQueuedIframeActivity(record) {
  queue = queue.filter((item) => item.record !== record);
}

/**
 * Turns one iframe event into an activity record, and stores it if it is one of the important ones.
 *
 * action: what happened, e.g. "FIELD_UPDATE", "OPEN". It is also the record's type.
 * summary: one readable sentence.
 * details: everything known about the event.
 * iframe: the iframe element it happened in.
 * origin, appPath, eventTime: from the embedded page, when the event came from the in-app tracker.
 */
export function logIframeActivity({ action, summary, details = {}, iframe = null, origin = null, appPath: eventPath = null, eventTime = null }) {
  const user = currentUser;
  const page = currentPage || {};
  const session = iframe ? sessionFor(iframe) : null;
  if (session) session.sequence += 1;
  const now = new Date().toISOString();

  // Custom HTML pages run in an iframe on the portal itself, so their path is the portal's own URL: not recorded.
  const appPath = origin === window.location.origin ? null : eventPath;

  const record = {
    type: action,
    user_id: user?._id || null,
    created_by: user?._id || null,
    public: !user?._id,
    data: {
      ip_address: null, // filled in by the server
      referer: `${window.location.origin}/`,
      origin: window.location.origin,
      login_authorization: user?.type ? user.type.toUpperCase() : null,
      login_method: null, // filled in by the server
      login_portal: "USER_PORTAL",
      workspace_id: page.workspace_id || null,
      workspace_name: page.workspace_name || null,
      group_id: page.group_id || null,
      group_name: page.group_name || null,
      page_id: page.page_id || null,
      page_name: page.page_name || null,
      form_id: page.form_id || null,
      form_name: page.form_name || null,
      document_id: page.document_id || null,
      document_name: page.document_name || null,
      created_by_first_name: user?.first_name || null,
      created_by_last_name: user?.last_name || null,
      created_by_email: user?.email || null,
      // Impersonation is filled in by the server.
      impersonate_by_user_id: null,
      impersonate_by_user_first_name: null,
      impersonate_by_user_last_name: null,
      impersonate_by_user_email: null,
      json_data: {
        action,
        summary,
        session_id: session?.id || null,
        sequence: session?.sequence || null,
        session_seconds: session ? Math.round((Date.now() - session.startedAt) / 1000) : null,
        page_type: page.page_type || null,
        frame: iframe
          ? {
              title: iframe.title || null,
              src: iframe.getAttribute("src"),
              origin: origin || originOf(iframe.getAttribute("src")),
              path: appPath,
            }
          : null,
        portal_url: window.location.href,
        event_time: eventTime || now,
        client: clientInfo(),
        details,
      },
    },
    created_at: now,
  };

  store(record, action, details, page.route);
  return record;
}
